import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { fetch } from '../utils/api-wrapper';
import FilterInputField from './filter-input-field';
import Modal from './modal';

const BRAND_COLOR = '#F39800';
const SUPPLIER_PAGE_SIZE = 10;
const EVIDENCE_PAGE_SIZE = 10;

const emptySummary = {
    evidence_count: 0,
    active_evidence_count: 0,
    inactive_evidence_count: 0,
    supplier_count: 0,
    manual_count: 0,
    normal_match_count: 0,
    supplier_fallback_count: 0,
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatLabel = (value) => value?.replaceAll('_', ' ') || '';

const headerCellSx = {
    backgroundColor: '#F7F8FA',
    borderBottom: '1px solid #E4E7EC',
    color: '#475467',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    py: 1.5,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    borderBottom: '1px solid #EAECF0',
    color: '#344054',
    py: 1.5,
    verticalAlign: 'middle',
};

const actionButtonSx = {
    color: '#344054',
    '&:hover': {
        backgroundColor: 'transparent',
        color: '#B86F00',
    },
};

const deleteButtonSx = {
    color: '#344054',
    '&:hover': {
        backgroundColor: 'transparent',
        color: '#B42318',
    },
};

const SupplierFallbackMonitor = ({ data, onOpenInvoice }) => {
    const t = data.t;
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [totalSuppliers, setTotalSuppliers] = useState(0);
    const [summary, setSummary] = useState(emptySummary);
    const [expandedSupplier, setExpandedSupplier] = useState(null);
    const [evidenceBySupplier, setEvidenceBySupplier] = useState({});
    const [updatingEvidence, setUpdatingEvidence] = useState(null);
    const [activationError, setActivationError] = useState(false);
    const [pendingDeactivation, setPendingDeactivation] = useState(null);
    const [deletingEvidence, setDeletingEvidence] = useState(null);
    const [deleteError, setDeleteError] = useState(false);
    const [pendingDeletion, setPendingDeletion] = useState(null);

    const loadSuppliers = useCallback(() => {
        setLoading(true);
        setError(false);
        const query = new URLSearchParams({
            start: page * SUPPLIER_PAGE_SIZE,
            page_size: SUPPLIER_PAGE_SIZE,
            search,
        });
        fetch(
            `/monitor/supplier-fallback/suppliers/?${query.toString()}`,
            'GET',
            {},
            response => {
                setSuppliers(response.data.suppliers || []);
                setTotalSuppliers(response.data.meta?.totalRowCount || 0);
                setSummary(response.data.meta?.summary || emptySummary);
                setLoading(false);
            },
            () => {
                setError(true);
                setLoading(false);
            },
            false,
        );
    }, [page, search]);

    useEffect(() => {
        loadSuppliers();
    }, [loadSuppliers]);

    const loadEvidence = (supplierId, evidencePage = 0) => {
        setEvidenceBySupplier(previous => ({
            ...previous,
            [supplierId]: {
                ...(previous[supplierId] || {}),
                loading: true,
                error: false,
                page: evidencePage,
            },
        }));
        const query = new URLSearchParams({
            start: evidencePage * EVIDENCE_PAGE_SIZE,
            page_size: EVIDENCE_PAGE_SIZE,
        });
        fetch(
            `/monitor/supplier-fallback/suppliers/${encodeURIComponent(supplierId)}/evidence/?${query.toString()}`,
            'GET',
            {},
            response => {
                setEvidenceBySupplier(previous => ({
                    ...previous,
                    [supplierId]: {
                        loading: false,
                        error: false,
                        page: evidencePage,
                        rows: response.data.evidence || [],
                        total: response.data.meta?.totalRowCount || 0,
                    },
                }));
            },
            () => {
                setEvidenceBySupplier(previous => ({
                    ...previous,
                    [supplierId]: {
                        ...(previous[supplierId] || {}),
                        loading: false,
                        error: true,
                        page: evidencePage,
                    },
                }));
            },
            false,
        );
    };

    const toggleSupplier = (supplierId) => {
        if (expandedSupplier === supplierId) {
            setExpandedSupplier(null);
            return;
        }
        setExpandedSupplier(supplierId);
        if (!evidenceBySupplier[supplierId]) loadEvidence(supplierId);
    };

    const updateSearch = value => {
        setPage(0);
        setExpandedSupplier(null);
        setSearch(value);
    };

    const updateFingerprintActive = (supplierId, evidenceId, active) => {
        setUpdatingEvidence(evidenceId);
        setActivationError(false);
        fetch(
            `/monitor/supplier-fallback/evidence/${encodeURIComponent(evidenceId)}/active/`,
            'PUT',
            { active },
            () => {
                setEvidenceBySupplier(previous => ({
                    ...previous,
                    [supplierId]: {
                        ...previous[supplierId],
                        rows: (previous[supplierId]?.rows || []).map(row => (
                            row.id_evidence === evidenceId ? { ...row, active } : row
                        )),
                    },
                }));
                const activeDelta = active ? 1 : -1;
                setSuppliers(previous => previous.map(supplier => (
                    supplier.id_supplier === supplierId
                        ? {
                            ...supplier,
                            active_evidence_count: Math.max(0, (supplier.active_evidence_count || 0) + activeDelta),
                            inactive_evidence_count: Math.max(0, (supplier.inactive_evidence_count || 0) - activeDelta),
                        }
                        : supplier
                )));
                setSummary(previous => ({
                    ...previous,
                    active_evidence_count: Math.max(0, (previous.active_evidence_count || 0) + activeDelta),
                    inactive_evidence_count: Math.max(0, (previous.inactive_evidence_count || 0) - activeDelta),
                }));
                setUpdatingEvidence(null);
            },
            () => {
                setActivationError(true);
                setUpdatingEvidence(null);
            },
            false,
        );
    };

    const setFingerprintActive = (supplierId, evidenceId, active) => {
        if (!active) {
            setPendingDeactivation({ supplierId, evidenceId });
            return;
        }
        updateFingerprintActive(supplierId, evidenceId, true);
    };

    const confirmDeactivation = () => {
        const fingerprint = pendingDeactivation;
        setPendingDeactivation(null);
        updateFingerprintActive(fingerprint.supplierId, fingerprint.evidenceId, false);
    };

    const deleteFingerprint = (supplierId, evidenceId) => {
        // The row count drives where the expanded panel lands afterwards, so read
        // it before the request rather than from the post-delete closure.
        const evidenceState = evidenceBySupplier[supplierId] || {};
        setDeletingEvidence(evidenceId);
        setDeleteError(false);
        fetch(
            `/monitor/supplier-fallback/evidence/${encodeURIComponent(evidenceId)}/`,
            'DELETE',
            {},
            () => {
                setDeletingEvidence(null);
                const remaining = Math.max(0, (evidenceState.total || 0) - 1);
                if (remaining === 0) {
                    // The supplier drops out of the list once its last fingerprint is gone.
                    setExpandedSupplier(null);
                    setEvidenceBySupplier(previous => {
                        const next = { ...previous };
                        delete next[supplierId];
                        return next;
                    });
                } else {
                    const lastPage = Math.ceil(remaining / EVIDENCE_PAGE_SIZE) - 1;
                    loadEvidence(supplierId, Math.min(evidenceState.page || 0, lastPage));
                }
                loadSuppliers();
            },
            () => {
                setDeleteError(true);
                setDeletingEvidence(null);
            },
            false,
        );
    };

    const confirmDeletion = () => {
        const fingerprint = pendingDeletion;
        setPendingDeletion(null);
        deleteFingerprint(fingerprint.supplierId, fingerprint.evidenceId);
    };

    return (
        <Paper
            component="section"
            elevation={0}
            aria-labelledby="supplier-fallback-title"
            // Bootstrap cards span their column before margins are applied.
            style={{ width: '100%' }}
            sx={{
                border: '1px solid #E4E7EC',
                borderRadius: '30px',
                // Match the horizontal inset used by the other monitor cards.
                mx: 1,
                my: 3,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Box>
                        <Typography id="supplier-fallback-title" color="#101828" fontSize="1.2rem" fontWeight={700}>
                            {t.read('supplier_fallback_monitor.title')}
                        </Typography>
                        <Typography color="#667085" fontSize="0.875rem">
                            {t.read('supplier_fallback_monitor.description')}
                        </Typography>
                    </Box>
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <MetricCard
                        label={t.read('supplier_fallback_monitor.evidence_created')}
                        value={summary.evidence_count}
                    />
                    <MetricCard
                        label={t.read('supplier_fallback_monitor.active_fingerprints')}
                        value={summary.active_evidence_count}
                    />
                    <MetricCard
                        label={t.read('supplier_fallback_monitor.inactive_fingerprints')}
                        value={summary.inactive_evidence_count}
                    />
                    <MetricCard
                        label={t.read('supplier_fallback_monitor.suppliers_learned')}
                        value={summary.supplier_count}
                    />
                </Grid>
            </Box>

            <Box
                sx={{
                    alignItems: 'center',
                    borderBottom: '1px solid #E4E7EC',
                    borderTop: '1px solid #E4E7EC',
                    display: 'flex',
                    px: { xs: 2, md: 3 },
                    py: 2,
                }}
            >
                <FilterInputField
                    filterValue={search}
                    setFilterValue={updateSearch}
                    placeholder={t.read('supplier_fallback_monitor.search_placeholder')}
                />
            </Box>

            {error && (
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={loadSuppliers}>{t.read('supplier_fallback_monitor.retry')}</Button>}
                    sx={{ borderRadius: 0 }}
                >
                    {t.read('supplier_fallback_monitor.load_error')}
                </Alert>
            )}

            {activationError && (
                <Alert severity="error" onClose={() => setActivationError(false)} sx={{ borderRadius: 0 }}>
                    {t.read('supplier_fallback_monitor.activation_error')}
                </Alert>
            )}

            {deleteError && (
                <Alert severity="error" onClose={() => setDeleteError(false)} sx={{ borderRadius: 0 }}>
                    {t.read('supplier_fallback_monitor.delete_error')}
                </Alert>
            )}

            <TableContainer>
                <Table aria-label={t.read('supplier_fallback_monitor.title')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.supplier')}</TableCell>
                            <TableCell align="center" sx={headerCellSx}>{t.read('supplier_fallback_monitor.evidence')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.origins')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.sources')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.first_learned')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.last_learned')}</TableCell>
                            <TableCell align="right" sx={headerCellSx}>{t.read('supplier_fallback_monitor.details')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ border: 0, py: 8, textAlign: 'center' }}>
                                    <CircularProgress size={28} sx={{ color: BRAND_COLOR }} />
                                    <Typography color="#667085" fontSize="0.875rem" sx={{ mt: 1.5 }}>
                                        {t.read('supplier_fallback_monitor.loading')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {!loading && !error && suppliers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ border: 0, py: 8, textAlign: 'center' }}>
                                    <Typography color="#667085" fontSize="0.9rem" sx={{ mt: 1 }}>
                                        {t.read('supplier_fallback_monitor.no_evidence')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {!loading && !error && suppliers.map(supplier => {
                            const isExpanded = expandedSupplier === supplier.id_supplier;
                            return (
                                <React.Fragment key={supplier.id_supplier}>
                                    <TableRow hover sx={{ '&:last-of-type td': { borderBottom: isExpanded ? undefined : 0 } }}>
                                        <TableCell sx={bodyCellSx}>
                                            <Stack alignItems="baseline" direction="row" spacing={0.75}>
                                                <Typography color="#101828" fontSize="0.875rem" fontWeight={600} noWrap>
                                                    {supplier.supplier_name || t.read('supplier_fallback_monitor.unknown_supplier')}
                                                </Typography>
                                                {!supplier.supplier_active && (
                                                    <Typography color="#B42318" fontSize="0.7rem">
                                                        {t.read('supplier_fallback_monitor.inactive')}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography color="#667085" fontSize="0.75rem">
                                                {supplier.supplier_number || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={bodyCellSx}>
                                            <Typography fontSize="0.85rem" fontWeight={700}>
                                                {supplier.active_evidence_count}/{supplier.evidence_count}
                                            </Typography>
                                            {(supplier.inactive_evidence_count || 0) > 0 && (
                                                <Typography color="#B42318" fontSize="0.68rem">
                                                    {supplier.inactive_evidence_count} {t.read('supplier_fallback_monitor.inactive').toLowerCase()}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <OriginSummary supplier={supplier} t={t} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <LabelList items={supplier.fingerprint_sources} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <DateValue value={supplier.first_evidence_at} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <DateValue value={supplier.last_evidence_at} />
                                        </TableCell>
                                        <TableCell align="right" sx={bodyCellSx}>
                                            <Tooltip title={isExpanded ? t.read('supplier_fallback_monitor.hide_evidence') : t.read('supplier_fallback_monitor.show_evidence')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleSupplier(supplier.id_supplier)}
                                                    aria-expanded={isExpanded}
                                                    aria-label={isExpanded ? t.read('supplier_fallback_monitor.hide_evidence') : t.read('supplier_fallback_monitor.show_evidence')}
                                                    sx={actionButtonSx}
                                                >
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <EvidenceDetails
                                                    state={evidenceBySupplier[supplier.id_supplier]}
                                                    supplierId={supplier.id_supplier}
                                                    supplier={supplier}
                                                    loadEvidence={loadEvidence}
                                                    onOpenInvoice={onOpenInvoice}
                                                    onSetActive={setFingerprintActive}
                                                    onDelete={(targetSupplierId, evidenceId) => setPendingDeletion({
                                                        supplierId: targetSupplierId,
                                                        evidenceId,
                                                    })}
                                                    updatingEvidence={updatingEvidence}
                                                    deletingEvidence={deletingEvidence}
                                                    t={t}
                                                />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {!loading && !error && totalSuppliers > 0 && (
                <TablePagination
                    component="div"
                    count={totalSuppliers}
                    page={page}
                    rowsPerPage={SUPPLIER_PAGE_SIZE}
                    rowsPerPageOptions={[SUPPLIER_PAGE_SIZE]}
                    labelRowsPerPage={t.read('supplier_fallback_monitor.rows_per_page')}
                    onPageChange={(_event, nextPage) => {
                        setExpandedSupplier(null);
                        setPage(nextPage);
                    }}
                    sx={{ borderTop: '1px solid #E4E7EC' }}
                />
            )}

            {pendingDeactivation && (
                <Modal
                    data={data}
                    title={t.read('confirm')}
                    okText={t.read('confirm')}
                    cancelText={t.read('cancel')}
                    okAction={confirmDeactivation}
                    cancelAction={() => setPendingDeactivation(null)}
                >
                    <p>{t.read('supplier_fallback_monitor.deactivate_confirm')}</p>
                </Modal>
            )}

            {pendingDeletion && (
                <Modal
                    data={data}
                    title={t.read('confirm')}
                    okText={t.read('confirm')}
                    cancelText={t.read('cancel')}
                    okAction={confirmDeletion}
                    cancelAction={() => setPendingDeletion(null)}
                >
                    <p>{t.read('supplier_fallback_monitor.delete_confirm')}</p>
                </Modal>
            )}
        </Paper>
    );
};

const MetricCard = ({ value, label }) => (
    <Grid item xs={12} sm={6} lg={3}>
        <Box sx={{ borderLeft: '2px solid #D0D5DD', pl: 1.5, py: 0.25 }}>
            <Typography color="#101828" fontSize="1.4rem" fontWeight={700} lineHeight={1.15}>
                {value || 0}
            </Typography>
            <Typography color="#667085" fontSize="0.75rem" sx={{ mt: 0.4 }}>
                {label}
            </Typography>
        </Box>
    </Grid>
);

const OriginSummary = ({ supplier, t }) => {
    const origins = [
        { count: supplier.manual_count, label: t.read('supplier_fallback_monitor.manual') },
        { count: supplier.normal_match_count, label: t.read('supplier_fallback_monitor.normal_match') },
        { count: supplier.supplier_fallback_count, label: t.read('supplier_fallback_monitor.supplier_fallback') },
    ].filter(origin => origin.count > 0);

    return origins.length > 0 ? (
        <Typography color="#475467" fontSize="0.75rem">
            {origins.map(origin => `${origin.label} ${origin.count}`).join(' · ')}
        </Typography>
    ) : '—';
};

const LabelList = ({ items }) => (
    <Typography color="#475467" fontSize="0.75rem">
        {items?.length ? items.map(formatLabel).join(', ') : '—'}
    </Typography>
);

const DateValue = ({ value }) => {
    const formatted = formatDate(value);
    if (formatted === '—') return formatted;
    const date = new Date(value);
    return (
        <Box>
            <Typography color="#344054" fontSize="0.8rem" whiteSpace="nowrap">
                {date.toLocaleDateString()}
            </Typography>
            <Typography color="#98A2B3" fontSize="0.7rem" whiteSpace="nowrap">
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
        </Box>
    );
};

const EvidenceDetails = ({ state, supplierId, supplier, loadEvidence, onOpenInvoice, onSetActive, onDelete, updatingEvidence, deletingEvidence, t }) => {
    if (!state || state.loading) {
        return (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
                <CircularProgress size={24} sx={{ color: BRAND_COLOR }} />
                <Typography color="#667085" fontSize="0.8rem">{t.read('supplier_fallback_monitor.loading')}</Typography>
            </Stack>
        );
    }
    if (state.error) {
        return (
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={() => loadEvidence(supplierId, state.page || 0)}>{t.read('supplier_fallback_monitor.retry')}</Button>}
                sx={{ borderRadius: 0 }}
            >
                {t.read('supplier_fallback_monitor.load_error')}
            </Alert>
        );
    }

    return (
        <Box sx={{ borderBottom: '1px solid #E4E7EC', borderTop: '1px solid #E4E7EC', px: { xs: 1.5, md: 3 }, py: 2.5 }}>
            <Typography color="#344054" fontSize="0.85rem" fontWeight={700} sx={{ mb: 1.5 }}>
                {t.read('supplier_fallback_monitor.evidence_details')} · {supplier.supplier_name || t.read('supplier_fallback_monitor.unknown_supplier')}
            </Typography>
            <TableContainer sx={{ border: '1px solid #E4E7EC' }}>
                <Table size="small" aria-label={t.read('supplier_fallback_monitor.evidence_details')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.invoice')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.correction')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.learned_at')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.learned_by')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.origin')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.components')}</TableCell>
                            <TableCell sx={headerCellSx}>{t.read('supplier_fallback_monitor.sources')}</TableCell>
                            <TableCell align="center" sx={headerCellSx}>{t.read('supplier_fallback_monitor.version')}</TableCell>
                            <TableCell align="center" sx={headerCellSx}>{t.read('supplier_fallback_monitor.status')}</TableCell>
                            <TableCell align="right" sx={headerCellSx} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!state.rows?.length && (
                            <TableRow>
                                <TableCell colSpan={10} sx={{ border: 0, color: '#667085', py: 4, textAlign: 'center' }}>
                                    {t.read('supplier_fallback_monitor.no_evidence')}
                                </TableCell>
                            </TableRow>
                        )}
                        {state.rows?.map(evidence => (
                            <TableRow hover key={evidence.id_evidence}>
                                <TableCell sx={bodyCellSx}>
                                    <Typography color="#101828" fontSize="0.8rem" fontWeight={600}>
                                        {evidence.invoice_number || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    {evidence.learning_type === 'supplier_correction'
                                        ? (
                                            <SupplierCorrection
                                                incorrect={evidence.incorrect_supplier}
                                                corrected={supplier}
                                                t={t}
                                            />
                                        )
                                        : '—'}
                                </TableCell>
                                <TableCell sx={bodyCellSx}><DateValue value={evidence.validated_at} /></TableCell>
                                <TableCell sx={bodyCellSx}>{evidence.validated_by || '—'}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Typography color="#475467" fontSize="0.75rem">
                                        {t.read(`supplier_fallback_monitor.${evidence.resolution_source}`)}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={bodyCellSx}><LabelList items={evidence.component_types} /></TableCell>
                                <TableCell sx={bodyCellSx}><LabelList items={evidence.fingerprint_sources} /></TableCell>
                                <TableCell align="center" sx={bodyCellSx}>
                                    <Typography fontSize="0.75rem" fontWeight={600}>v{evidence.signature_version}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={bodyCellSx}>
                                    <Tooltip title={t.read(evidence.active === false
                                        ? 'supplier_fallback_monitor.activate_fingerprint'
                                        : 'supplier_fallback_monitor.deactivate_fingerprint')}
                                    >
                                        <Switch
                                            checked={evidence.active !== false}
                                            disabled={updatingEvidence === evidence.id_evidence}
                                            onChange={event => onSetActive(
                                                supplierId,
                                                evidence.id_evidence,
                                                event.target.checked,
                                            )}
                                            inputProps={{
                                                'aria-label': t.read(evidence.active === false
                                                    ? 'supplier_fallback_monitor.activate_fingerprint'
                                                    : 'supplier_fallback_monitor.deactivate_fingerprint'),
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_COLOR },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND_COLOR },
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right" sx={bodyCellSx}>
                                    <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                        <Tooltip title={t.read('supplier_fallback_monitor.open_invoice')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => onOpenInvoice(evidence.id_invoice)}
                                                aria-label={t.read('supplier_fallback_monitor.open_invoice')}
                                                sx={actionButtonSx}
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t.read('supplier_fallback_monitor.delete_fingerprint')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={deletingEvidence === evidence.id_evidence}
                                                    onClick={() => onDelete(supplierId, evidence.id_evidence)}
                                                    aria-label={t.read('supplier_fallback_monitor.delete_fingerprint')}
                                                    sx={deleteButtonSx}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {(state.total || 0) > 0 && (
                    <TablePagination
                        component="div"
                        count={state.total || 0}
                        page={state.page || 0}
                        rowsPerPage={EVIDENCE_PAGE_SIZE}
                        rowsPerPageOptions={[EVIDENCE_PAGE_SIZE]}
                        labelRowsPerPage={t.read('supplier_fallback_monitor.rows_per_page')}
                        onPageChange={(_event, nextPage) => loadEvidence(supplierId, nextPage)}
                    />
                )}
            </TableContainer>
        </Box>
    );
};

const SupplierCorrection = ({ incorrect, corrected, t }) => (
    <Stack alignItems="center" direction="row" spacing={1}>
        <Box>
            <Typography color="#B42318" fontSize="0.75rem" fontWeight={600}>
                {incorrect?.supplier_name || t.read('supplier_fallback_monitor.unknown_supplier')}
            </Typography>
            <Typography color="#667085" fontSize="0.68rem">
                {incorrect?.supplier_number || '—'}
            </Typography>
        </Box>
        <Typography aria-hidden="true" color="#98A2B3">→</Typography>
        <Box>
            <Typography color="#027A48" fontSize="0.75rem" fontWeight={600}>
                {corrected?.supplier_name || t.read('supplier_fallback_monitor.unknown_supplier')}
            </Typography>
            <Typography color="#667085" fontSize="0.68rem">
                {corrected?.supplier_number || '—'}
            </Typography>
        </Box>
    </Stack>
);

export { formatDate, formatLabel };
export default SupplierFallbackMonitor;
